import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FlowLogo } from '../components/FlowLogo'
import SEO from '../components/SEO'

const Terms: React.FC = () => {
  const { t } = useTranslation()
  
  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [])
  
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Terms of Service - MortgageFlow"
        description="Read MortgageFlow's terms of service to understand the legal agreement between you and our mortgage broker CRM platform."
        url="https://mortgageflow.pro/terms"
      />
      
      {/* Header */}
      <nav className="relative z-50 px-6 py-5 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3">
            <FlowLogo size="md" animate={false} />
            <div>
              <h1 className="text-lg font-semibold text-gray-900">MortgageFlow</h1>
            </div>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center px-5 py-2 text-gray-600 hover:text-gray-900 font-medium transition-colors duration-200"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </Link>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t('legal.terms.title')}
          </h1>
          <p className="text-gray-600 mb-8">
            {t('legal.terms.lastUpdated')}: {t('legal.terms.updateDate')}
          </p>

          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('legal.terms.introduction.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('legal.terms.introduction.p1')}
            </p>
            <p className="text-gray-700 leading-relaxed">
              {t('legal.terms.introduction.p2')}
            </p>
          </section>

          {/* Acceptance of Terms */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('legal.terms.acceptance.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed">
              {t('legal.terms.acceptance.p1')}
            </p>
          </section>

          {/* Service Description */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('legal.terms.service.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('legal.terms.service.p1')}
            </p>
            <ul className="list-disc list-inside text-gray-700 leading-relaxed space-y-2">
              <li>{t('legal.terms.service.item1')}</li>
              <li>{t('legal.terms.service.item2')}</li>
              <li>{t('legal.terms.service.item3')}</li>
              <li>{t('legal.terms.service.item4')}</li>
              <li>{t('legal.terms.service.item5')}</li>
              <li>{t('legal.terms.service.item6')}</li>
              <li>{t('legal.terms.service.item7')}</li>
              <li>{t('legal.terms.service.item8')}</li>
              <li>{t('legal.terms.service.item9')}</li>
              <li>{t('legal.terms.service.item10')}</li>
              <li>{t('legal.terms.service.item11')}</li>
              <li>{t('legal.terms.service.item12')}</li>
            </ul>
          </section>

          {/* User Accounts */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('legal.terms.accounts.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('legal.terms.accounts.p1')}
            </p>
            <ul className="list-disc list-inside text-gray-700 leading-relaxed space-y-2">
              <li>{t('legal.terms.accounts.item1')}</li>
              <li>{t('legal.terms.accounts.item2')}</li>
              <li>{t('legal.terms.accounts.item3')}</li>
              <li>{t('legal.terms.accounts.item4')}</li>
            </ul>
          </section>

          {/* Subscription and Payment */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('legal.terms.payment.title')}
            </h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              {t('legal.terms.payment.freeTrial.title')}
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('legal.terms.payment.freeTrial.p1')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              {t('legal.terms.payment.pricing.title')}
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('legal.terms.payment.pricing.p1')}
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('legal.terms.payment.pricing.p2')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              {t('legal.terms.payment.billing.title')}
            </h3>
            <ul className="list-disc list-inside text-gray-700 leading-relaxed mb-4 space-y-2">
              <li>{t('legal.terms.payment.billing.item1')}</li>
              <li>{t('legal.terms.payment.billing.item2')}</li>
              <li>{t('legal.terms.payment.billing.item3')}</li>
              <li>{t('legal.terms.payment.billing.item4')}</li>
              <li>{t('legal.terms.payment.billing.item5')}</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              {t('legal.terms.payment.cancellation.title')}
            </h3>
            <p className="text-gray-700 leading-relaxed">
              {t('legal.terms.payment.cancellation.p1')}
            </p>
          </section>

          {/* User Obligations */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('legal.terms.obligations.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('legal.terms.obligations.p1')}
            </p>
            <ul className="list-disc list-inside text-gray-700 leading-relaxed space-y-2">
              <li>{t('legal.terms.obligations.item1')}</li>
              <li>{t('legal.terms.obligations.item2')}</li>
              <li>{t('legal.terms.obligations.item3')}</li>
              <li>{t('legal.terms.obligations.item4')}</li>
              <li>{t('legal.terms.obligations.item5')}</li>
              <li>{t('legal.terms.obligations.item6')}</li>
            </ul>
          </section>

          {/* Intellectual Property */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('legal.terms.intellectual.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('legal.terms.intellectual.p1')}
            </p>
            <p className="text-gray-700 leading-relaxed">
              {t('legal.terms.intellectual.p2')}
            </p>
          </section>

          {/* Data and Privacy */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('legal.terms.data.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('legal.terms.data.p1')}
            </p>
            <p className="text-gray-700 leading-relaxed">
              {t('legal.terms.data.p2')}
            </p>
          </section>

          {/* Prohibited Activities */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('legal.terms.prohibited.title')}
            </h2>
            <ul className="list-disc list-inside text-gray-700 leading-relaxed space-y-2">
              <li>{t('legal.terms.prohibited.item1')}</li>
              <li>{t('legal.terms.prohibited.item2')}</li>
              <li>{t('legal.terms.prohibited.item3')}</li>
              <li>{t('legal.terms.prohibited.item4')}</li>
              <li>{t('legal.terms.prohibited.item5')}</li>
              <li>{t('legal.terms.prohibited.item6')}</li>
              <li>{t('legal.terms.prohibited.item7')}</li>
            </ul>
          </section>

          {/* Service Availability */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('legal.terms.availability.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('legal.terms.availability.p1')}
            </p>
            <p className="text-gray-700 leading-relaxed">
              {t('legal.terms.availability.p2')}
            </p>
          </section>

          {/* API Access */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('legal.terms.apiAccess.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('legal.terms.apiAccess.p1')}
            </p>
            <p className="text-gray-700 leading-relaxed">
              {t('legal.terms.apiAccess.p2')}
            </p>
          </section>

          {/* Limitation of Liability */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('legal.terms.liability.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('legal.terms.liability.p1')}
            </p>
            <p className="text-gray-700 leading-relaxed">
              {t('legal.terms.liability.p2')}
            </p>
          </section>

          {/* Warranty Disclaimer */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('legal.terms.warranty.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('legal.terms.warranty.p1')}
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('legal.terms.warranty.p2')}
            </p>
            <p className="text-gray-700 leading-relaxed">
              {t('legal.terms.warranty.p3')}
            </p>
          </section>

          {/* Indemnification */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('legal.terms.indemnification.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed">
              {t('legal.terms.indemnification.p1')}
            </p>
          </section>

          {/* Termination */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('legal.terms.termination.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('legal.terms.termination.p1')}
            </p>
            <p className="text-gray-700 leading-relaxed">
              {t('legal.terms.termination.p2')}
            </p>
          </section>

          {/* Data Retention */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('legal.terms.dataRetention.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('legal.terms.dataRetention.p1')}
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('legal.terms.dataRetention.p2')}
            </p>
            <p className="text-gray-700 leading-relaxed">
              {t('legal.terms.dataRetention.p3')}
            </p>
          </section>

          {/* Governing Law */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('legal.terms.governing.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed">
              {t('legal.terms.governing.p1')}
            </p>
          </section>

          {/* Changes to Terms */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('legal.terms.modifications.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed">
              {t('legal.terms.modifications.p1')}
            </p>
          </section>

          {/* Contact */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('legal.terms.contact.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('legal.terms.contact.p1')}
            </p>
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <p className="text-gray-700">
                <strong>MortgageFlow.pro</strong><br />
                {t('legal.terms.contact.email')}: <a href="mailto:legal@mortgageflow.pro" className="text-gray-900 hover:text-blue-700">legal@mortgageflow.pro</a><br />
                {t('legal.terms.contact.address')}: {t('legal.terms.contact.addressValue')}
              </p>
            </div>
          </section>
        </div>

        {/* Back to home link */}
        <div className="text-center mt-8">
          <Link
            to="/"
            className="inline-flex items-center text-gray-900 hover:text-blue-700 font-medium"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {t('legal.backToHome')}
          </Link>
        </div>
      </div>
    </div>
  )
}

export default Terms

